import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetUserEntryQuery } from "services/auth/authService";
import { SchemaMods, SchemaImages } from "types/carTypes";
import { scrollToTop, loadExistingData, createFormData } from "helpers/utils";
import PSPage from "components/globals/ui/layout/Page";
import { EntryLoading, EntryError } from "components/globals/EntryStates";
import { ProgressBarCars, FormStage } from "components/globals/forms/ORSFormLayout";
import Pane from "components/modals/Pane";
import { FaPlus } from "react-icons/fa6";
import DeleteEntryUI from "components/globals/utils/DeleteEntryUI";
import {AddonForm} from "components/globals/forms/GarageCarAddon";
import PSGallery from "components/globals/PSGallery";
import { useSelector, useDispatch } from "react-redux";
import { ErrorPane } from "components/modals/Panes";
import { updateEntry } from "store/authSlice";

export default function CreateCarMods() {
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [newGalleryPane, setNewGalleryPane] = useState(false);
	const [postData, updatePostData] = useState(SchemaMods);
	const navigate = useNavigate();
	const userToken = useSelector((state) => state.auth.userToken);
	const dispatch = useDispatch();


	const {
		data: existingEntryData,
		loading: entryLoading,
		error: entryError,
		refetch,
	} = useGetUserEntryQuery(`cargallery/${id}`);

	const {
		data: existingCarData
	} = useGetUserEntryQuery(`car/${id}`);

	useEffect(() => {
		if (existingEntryData) {
			loadExistingData(updatePostData, SchemaMods, existingEntryData);
			setLoading(false);
		}
	}, [existingEntryData]);

	useEffect(() => {
		if (existingCarData) {
			loadExistingData(updatePostData, SchemaImages, existingCarData);
			setLoading(false);
		}
	}, [existingCarData]);

	const updateSelectedGalleryFiles = (images) => {
		setNewPostDataProperty("gallery", images);
	};

	const setNewPostDataProperty = (context, val) => {
		updatePostData((prevData) => ({
			...prevData,
			[context]: val,
		}));
	};

	const submitForm = async () => {
		if (!userToken || (!id)) return;

		setLoading(true);
		setErrorMessage(null);

		let fd = createFormData(postData, SchemaImages);
		fd.append("internal_id", id);

		try {
			await dispatch(updateEntry({ data: fd, entry_type: "garagecar" })).unwrap();
			navigate(`/edit/car/associations/${id}`);
		} catch (err) {
			setErrorMessage(err.message || "Failed. Please try again.");
		} finally {
			setLoading(false);
			scrollToTop();
		}
	};

	const hideNewPane = () => {
		setNewGalleryPane(false);
	}

	const clearErrorMessage = () => {
		setErrorMessage(null);
	};

	const onSuccess = () => {
		hideNewPane();
		refetch();
	}

	if (entryLoading) {
		return <EntryLoading />;
	}
	if (entryError) {
		return <EntryError msg="Failed to load." />;
	}

	return (
		<PSPage>

			<ErrorPane
				visible={errorMessage}
				message={errorMessage}
				onClose={clearErrorMessage}
			/>

			<div className="relative">

				<ProgressBarCars stage={3} id={id} />

				<div className="mt-[60px]">

					{/* panel 4: mods */}
					{!loading && (
						<FormStage 
							heading={`Update garage car: ${existingCarData?.entry.title}`}
							subheading="Images" 
							onSave={submitForm}
							stage={3}
							stages={5}
							previousUrl={`/edit/car/optional/${id}`}
						>

							
							<h2 className=" text-[18px] mb-2">Main Image Gallery</h2>
							<p className=" text-[13px] text-grey mb-3">The first image uploaded here will be assigned as the profile photo</p>
							<PSGallery
								initialImages={postData.gallery || []}
								onImagesChange={updateSelectedGalleryFiles}
							/>

							<hr className="mt-6" />

							<h2 className=" text-[18px] mb-2 mt-6">Additional Image Galleries</h2>

							{/* list galleries here */}
							{existingEntryData?.entries.map((gallery) => (
								<div className="border-border border-[1px] rounded-default p-3 my-1 flex items-center space-between">
									<span className="grow text-[16px] ">{gallery.type} - {gallery.title}</span>
									<div>
										<DeleteEntryUI noText entry={gallery} refetch onRefetch={refetch} />
									</div>
								</div>
							))}

							{/* always a create-new */}
							<NewGallery onAddNewGallery={setNewGalleryPane} />

							<Pane visible={newGalleryPane} onClose={hideNewPane}>
								<div className="relative mt-6">
									<AddonForm addonType="cargallery" id={id} onSuccess={onSuccess} />
								</div>
							</Pane>
							

						</FormStage>
					)}

				</div>
			</div>
		</PSPage>
	);
}

const NewGallery = ({onAddNewGallery}) => {
	return (
		<div 
			onClick={onAddNewGallery}
			className="
				outline-3 
				outline-offset-2 
				outline-dashed 
				p-6 mt-6
				outline-grey 
				rounded-default 
				text-grey
				flex justify-center
				cursor-pointer">
			<div className="flex items-center">
				<FaPlus className="text-2xl " />
				<span className="block ml-2 text-lg ">NEW GALLERY</span>
			</div>
		</div>
	)
}

